import { useMutation, useQueryClient } from '@tanstack/react-query'
import { errorMessage, useTRPC } from '../lib/trpc.js'
import { Badge, Button, VISIT_STATUS_TONE, humanise } from './ui.js'

/**
 * Check in and out of a booking.
 *
 * One button at a time, showing only the next step, because this is used
 * one-handed at a front door. A row of five status choices is a decision the
 * person does not need to make. The labels speak in the technician's own
 * voice — "On my way", not "Set status to en route".
 *
 * Each press writes to `ScheduledVisit`, which emits a change notification,
 * so the office sees the technician arrive without refreshing anything.
 */

type VisitStatus =
  | 'PROPOSED'
  | 'CONFIRMED'
  | 'EN_ROUTE'
  | 'ON_SITE'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'NO_SHOW'

type CheckInStatus = 'EN_ROUTE' | 'ON_SITE' | 'COMPLETED'

/** The single next step, or null when there is nothing left to do. */
const NEXT_STEP: Partial<Record<VisitStatus, { status: CheckInStatus; label: string }>> = {
  PROPOSED: { status: 'EN_ROUTE', label: 'On my way' },
  CONFIRMED: { status: 'EN_ROUTE', label: 'On my way' },
  EN_ROUTE: { status: 'ON_SITE', label: "I've arrived" },
  ON_SITE: { status: 'COMPLETED', label: 'Finished here' },
}

/** The next check-in label, shared with the board's quick menu. */
export function nextCheckIn(status: string): { status: CheckInStatus; label: string } | null {
  return NEXT_STEP[status as VisitStatus] ?? null
}

export function VisitActions({
  visitId,
  status,
  compact = false,
}: {
  visitId: string
  status: string
  compact?: boolean
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const update = useMutation(
    trpc.schedule.checkIn.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      },
    }),
  )

  const next = nextCheckIn(status)
  const tone = VISIT_STATUS_TONE[status] ?? 'neutral'

  if (!next) {
    return <Badge tone={tone}>{humanise(status)}</Badge>
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Badge tone={tone}>{humanise(status)}</Badge>
        <Button
          size={compact ? 'sm' : 'lg'}
          variant="primary"
          loading={update.isPending}
          onClick={() => update.mutate({ id: visitId, status: next.status })}
        >
          {next.label}
        </Button>
      </div>

      {/* Almost always the required-checklist refusal, and it names the items
          that are missing. Shown here rather than as a toast: the person is
          standing at the van and needs to read it, not catch it. */}
      {update.error ? (
        <p
          role="alert"
          className="rounded-md bg-red-50 px-2.5 py-2 text-xs text-red-800 ring-1 ring-red-200 ring-inset"
        >
          {errorMessage(update.error)}
        </p>
      ) : null}
    </div>
  )
}
